import React from "react";
import Seo from "../components/Seo";
import Loading from "../components/Loading";
import {Link} from "react-router-dom";
import "../css/Documentation.css";
import run from "../xmq_test_results/Chaos.json";

// One row per crash. The checks in the log are worded for a person reading it ("node2 serves:
// held from 35s to 45s, 3 look(s)"); the page only needs to know which of the three questions a
// check answers, so it sorts them by their prefix rather than keeping a second copy of the list.
function classify(cycle)
{
    const victim = cycle.victim;
    const serves = cycle.checks.filter(c => c.text.endsWith(" serves"));
    const survivor = serves.filter(c => c.text !== `${victim} serves`);
    const back = serves.filter(c => c.text === `${victim} serves`);
    const delivery = cycle.checks.filter(c => c.text.startsWith("a message crosses"));
    const failed = cycle.checks.filter(c => c.result !== "PASS");
    return {survivor, back, delivery, failed};
}

function Ratio({checks})
{
    const passed = checks.filter(c => c.result === "PASS").length;
    const colour = passed === checks.length ? "#1a7f37" : "#b42318";
    return <span style={{color: colour, fontFamily: "monospace"}}>{passed}/{checks.length}</span>;
}

export default class XMQ_tests_chaos extends React.Component
{
    render()
    {
        const cell = {border: "1px solid #ccc", padding: "4px 8px", verticalAlign: "top"};
        const head = {...cell, background: "#f0f0f0", fontWeight: "bold", textAlign: "left"};
        const table = {borderCollapse: "collapse", margin: "8px 0 16px 0"};
        const cycles = run.cycles || [];
        const allChecks = cycles.flatMap(c => c.checks).concat(run.final_checks || []);
        const totalPassed = allChecks.filter(c => c.result === "PASS").length;
        const totalFailed = allChecks.length - totalPassed;

        return <div key="tests-chaos" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="MQTT Cluster Survivability — Chaos Test"
                 description="A four-node XMQ cluster under load while each node is killed outright in turn: survivors keep serving, cross-node delivery resumes, the coordinator survives, membership stays whole."
                 keywords="MQTT cluster, MQTT high availability, chaos test, broker failover, XMQ"
                 path="/xmq_tests_chaos"/>
            <h1>MQTT Performance Tests: Cluster Survivability</h1>
            <h3>Killing Each Node Outright, Under Load</h3>

            <p>
                A throughput number says nothing about what happens when a node dies. This run asks a
                different question: with the cluster carrying traffic, does killing a node outright
                stop the cluster from serving? Every node is killed with <code>kill -9</code> &mdash; no
                handover, no lease released, nothing told to the cluster in advance &mdash; left down for
                20 seconds, and started again. Then the next node, and so on.
            </p>
            <p>
                The stand is the same four-node AWS cluster as the throughput results: one
                <code>c5n.2xlarge</code> per node in <code>us-east-1a</code>, the shared Redis and the
                accounts database outside the broker processes. Killing the broker here is killing a
                process, not a machine, which is what makes the question about the cluster rather than
                about AWS: the other three nodes, the store and the network are all still there.
            </p>
            <p>
                The run takes {run.duration || 600} seconds, with one crash every {run.cycle || 90} seconds:
                {" "}{cycles.length} crashes in all, each node in turn and the first two again. The two nodes
                carrying the load &mdash; publishers on one, subscribers on the other &mdash; are among the
                victims, so the test covers losing the node clients are attached to, not only a quiet one.
                The timeline is fixed rather than random: the same nodes are killed at the same seconds on
                every run, so a failure can be reproduced.
            </p>

            <h4>What the run does</h4>
            <p>
                The load is 100 publishers on one node and 100 subscribers on another, 200 topics, one
                subscriber each, 2 messages per second per publisher at QoS&nbsp;1 &mdash; about 200
                messages per second routed across the cluster, over the cluster links. While that runs,
                each crash cycle checks three things:
            </p>
            <ul>
                <li><b>the survivors keep serving</b> &mdash; a client connects to each surviving node, every
                    5 seconds, for the whole outage;</li>
                <li><b>the survivors keep delivering</b> &mdash; a message is published on one survivor and read on
                    another, every 10 seconds, crossing the cluster while a node is gone;</li>
                <li><b>the victim carries traffic again</b> &mdash; once it is back, the same connect and delivery
                    checks run against it.</li>
            </ul>
            <p>
                The geometry changes with each cycle, so every node in the cluster is at some point a
                survivor, a publisher's node, a subscriber's node, and the victim. A run also fails if a
                node's own log shows a crash, or if the cluster loses a member or its coordinator by the
                end.
            </p>

            <h4>Result</h4>
            {!run.cycles
                ? <Loading text="Loading results…"/>
                : <div>
                    <table style={table}>
                        <tbody>
                        <tr>
                            <td style={head}>Crash</td>
                            <td style={head}>Node killed</td>
                            <td style={head}>Down</td>
                            <td style={head}>Survivors serving</td>
                            <td style={head}>Delivery across survivors</td>
                            <td style={head}>Victim back in service</td>
                            <td style={head}>Cycle</td>
                        </tr>
                        {cycles.map(cycle => {
                            const {survivor, back, delivery, failed} = classify(cycle);
                            return <tr key={cycle.number}>
                                <td style={cell}>{cycle.number}</td>
                                <td style={cell}><code>{cycle.victim}</code> at {Math.round(cycle.stop_s)}s</td>
                                <td style={cell}>{cycle.down_s}s</td>
                                <td style={cell}><Ratio checks={survivor}/></td>
                                <td style={cell}><Ratio checks={delivery}/></td>
                                <td style={cell}><Ratio checks={back}/></td>
                                <td style={{...cell, color: failed.length ? "#b42318" : "#1a7f37", fontWeight: "bold"}}>
                                    {failed.length ? "FAIL" : "PASS"}
                                </td>
                            </tr>;
                        })}
                        </tbody>
                    </table>

                    <p>
                        <b>{totalPassed} of {totalPassed + totalFailed} checks passed, {totalFailed} failed.</b>
                        {" "}The cluster finished with every member present, a coordinator leading, and a message
                        still crossing from the node that was killed last to both survivors.
                    </p>

                    <table style={table}>
                        <tbody>
                        <tr><td style={head}>Closing checks</td><td style={head}>Result</td></tr>
                        {(run.final_checks || []).map((check, index) =>
                            <tr key={index}>
                                <td style={cell}>{check.text}</td>
                                <td style={{...cell, color: check.result === "PASS" ? "#1a7f37" : "#b42318"}}>
                                    {check.result}
                                </td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>}

            <h4>What this does and does not say</h4>
            <p>
                Publishing continued throughout. Clients whose node was killed reconnect on their own; the
                load's throughput dips while they do, and the delivery latency in the raw log is measured
                across the outage, so it is not a latency result and is not published as one. This page
                reports pass or fail on the cluster's behaviour, not a rate.
            </p>
            <p>
                The chaos load uses clean sessions (<code>clean_session=true</code>) on both publishers
                and subscribers. It asks whether the cluster keeps serving while nodes die; it does not
                ask whether a disconnected client's session state moves to another node. A client whose
                node is killed comes back and subscribes again, as a clean session does. Session
                migration with a persistent session is measured separately on the
                {" "}<Link to="/xmq_tests_cluster">Cluster</Link> page.
            </p>
            <p>
                The four-node AWS throughput results are on the
                {" "}<Link to="/xmq_tests_cluster">Cluster</Link> page. The runner, the load and the
                generated timelines are in the repository under <code>LoadTest/Cluster/</code>, and the
                systemd adapter used on these nodes is <code>LoadTest/AWS/chaos-systemd-node.sh</code>,
                so the run can be repeated as it was made.
            </p>
        </div>;
    }
}
